import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import LedgerTable from "@/components/ledger/LedgerTable";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import { propertyKey } from "@/lib/workbookImport";
import { buildLedgerRows, filterLedgerRows, formatMoney, houseName, ledgerTotals, tenantName } from "@/lib/ledger";

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const money = (v) => formatMoney(v).replace(/\.00$/, "");

function Section({ title, action, children }) {
  return (
    <section className="hh-panel p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="hh-section-title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function House() {
  const { id } = useParams();
  const [period, setPeriod] = useState("ytd");
  const { data, isLoading, refresh } = usePortfolioData([
    "properties",
    "tenants",
    "expenses",
    "rentPayments",
    "maintenance",
    "insurance",
    "licenses",
  ]);
  const property = data.properties.find((p) => String(p._id) === id);
  const key = property ? propertyKey(property.address || property.name) : null;
  const matches = (item, label) => String(item.propertyId || "") === id || (!item.propertyId && key && propertyKey(label) === key);

  const tenants = data.tenants.filter((t) => String(t.propertyId) === id);
  const activeTenants = tenants.filter((t) => t.isActive);
  const policies = data.insurance.filter((p) => matches(p, p.propertyLabel));
  const licenses = data.licenses.filter((l) => matches(l, l.propertyLabel));
  const jobs = data.maintenance
    .filter((m) => String(m.propertyId) === id)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const openJobs = jobs.filter((m) => !["completed", "cancelled"].includes(m.status));
  const doneJobs = jobs.filter((m) => m.status === "completed").slice(0, 5);

  const allRows = useMemo(() => buildLedgerRows(data).filter((row) => row.propertyId === id), [data, id]);
  const rows = useMemo(() => filterLedgerRows(allRows, { period }), [allRows, period]);
  const year = ledgerTotals(filterLedgerRows(allRows, { period: "ytd" }));

  if (!property) {
    return (
      <PageLayout title="House">
        <p className="text-sm text-muted-foreground">
          {isLoading ? "Loading…" : (
            <>
              House not found. <Link to="/houses" className="underline">Back to houses</Link>
            </>
          )}
        </p>
      </PageLayout>
    );
  }

  const rent = activeTenants.reduce((sum, t) => sum + Number(t.monthlyRent || 0), 0) || Number(property.monthlyRent || 0);
  const leaseEnd = activeTenants.map((t) => t.leaseEndDate).filter(Boolean).sort()[0];
  const address = [property.address, property.city, property.state, property.zipCode].filter(Boolean).join(", ");

  return (
    <PageLayout
      title={houseName(property)}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link to="/properties">Edit house</Link>
        </Button>
      }
    >
      <div className="-mt-3 mb-5 space-y-1">
        <Link to="/houses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Houses
        </Link>
        <p className="text-sm text-muted-foreground">
          {address}
          {rent ? ` · Rent ${money(rent)}/mo` : ""}
          {leaseEnd ? ` · Lease to ${fmtDate(leaseEnd)}` : ""}
          {property.mortgageMonthlyPayment ? ` · Mortgage ${money(property.mortgageMonthlyPayment)}` : ""}
          {" · "}
          <span className={year.net < 0 ? "text-rose-700 dark:text-rose-300" : ""}>Net this year {formatMoney(year.net, { sign: true }).replace(/\.00$/, "")}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section
          title="Tenants"
          action={
            <Button variant="ghost" size="sm" asChild>
              <Link to="/tenants">Manage</Link>
            </Button>
          }
        >
          {activeTenants.length ? (
            <ul className="space-y-3 text-sm">
              {activeTenants.map((tenant) => (
                <li key={tenant._id}>
                  <p className="font-medium">{tenantName(tenant)}</p>
                  <p className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                    {tenant.phone ? (
                      <a href={`tel:${tenant.phone}`} className="inline-flex items-center gap-1 hover:text-foreground">
                        <Phone className="h-3.5 w-3.5" /> {tenant.phone}
                      </a>
                    ) : null}
                    {tenant.email ? (
                      <a href={`mailto:${tenant.email}`} className="inline-flex items-center gap-1 hover:text-foreground">
                        <Mail className="h-3.5 w-3.5" /> {tenant.email}
                      </a>
                    ) : null}
                  </p>
                  <p className="text-muted-foreground">
                    {money(tenant.monthlyRent)}/mo · deposit {money(tenant.securityDeposit)} · {fmtDate(tenant.leaseStartDate)} – {fmtDate(tenant.leaseEndDate)}
                  </p>
                  {tenant.notes ? <p className="mt-1 text-xs text-muted-foreground">{tenant.notes}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Vacant. <Link to="/tenants" className="underline">Add a tenant</Link>
            </p>
          )}
        </Section>

        <Section title="Key info">
          <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Insurance</dt>
            <dd>
              {policies.length
                ? policies.map((p) => (
                    <span key={p._id} className="block">
                      {p.insuranceName} · {p.policyNumber} · renews {fmtDate(p.termEnd)} · {money(p.premium)}/yr
                    </span>
                  ))
                : <Link to="/insurance" className="text-muted-foreground underline">Add policy</Link>}
            </dd>
            <dt className="text-muted-foreground">License</dt>
            <dd>
              {licenses.length
                ? licenses.map((l) => (
                    <span key={l._id} className="block">
                      {l.category} {l.licenseNumber ? `· ${l.licenseNumber}` : ""} · expires {fmtDate(l.dateTo)}
                    </span>
                  ))
                : <span className="text-muted-foreground">—</span>}
            </dd>
            <dt className="text-muted-foreground">Lender</dt>
            <dd>
              {property.mortgageLender || property.mortgageMonthlyPayment
                ? [
                    property.mortgageLender,
                    property.mortgageMonthlyPayment ? `${money(property.mortgageMonthlyPayment)}/mo` : "",
                    property.mortgageDueDay ? `on the ${property.mortgageDueDay}` : "",
                  ].filter(Boolean).join(" · ")
                : <span className="text-muted-foreground">—</span>}
            </dd>
            {property.notes ? (
              <>
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="whitespace-pre-line">{property.notes}</dd>
              </>
            ) : null}
          </dl>
        </Section>
      </div>

      <div className="mb-3 mt-6 flex items-center justify-between gap-2">
        <h2 className="hh-section-title">Money</h2>
        <select
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
          aria-label="Period"
        >
          <option value="ytd">This year</option>
          <option value="12m">Last 12 months</option>
          <option value="all">Everything</option>
        </select>
      </div>
      <LedgerTable rows={rows} properties={data.properties} tenants={data.tenants} lockedHouse={id} onChanged={refresh} />

      <div className="mt-6">
        <Section
          title="Maintenance"
          action={
            <Button variant="ghost" size="sm" asChild>
              <Link to="/maintenance">Open maintenance</Link>
            </Button>
          }
        >
          {openJobs.length || doneJobs.length ? (
            <ul className="divide-y text-sm">
              {[...openJobs, ...doneJobs].map((job) => (
                <li key={job._id} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{job.title}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(job.createdAt)}</span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                      job.status === "completed" ? "bg-muted text-muted-foreground" : "bg-amber-500/15 text-amber-800 dark:text-amber-300"
                    }`}
                  >
                    {job.status === "inProgress" ? "In progress" : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No repairs logged for this house.</p>
          )}
        </Section>
      </div>
    </PageLayout>
  );
}
